/**
 * WispEvaluator — Race-focused position evaluation
 *
 * Wisp's value system:
 * - King proximity to goal is everything
 * - Non-king pieces are expendable tools (escorts, distractions, blockers)
 * - Phase-based weights prevent premature king rush
 * - King exposure penalty keeps king safe until escort pieces develop
 */

import { GameState, PieceType, IPiece, Position } from "../../types";

// Phase thresholds
const OPENING_END = 7;
const MIDGAME_END = 16;
const ENDGAME_PIECE_THRESHOLD = 3; // Jump to endgame if <= 3 pieces per side

// Piece values — Wisp barely cares about material
const PIECE_VALUES: Partial<Record<PieceType, number>> = {
  [PieceType.Bear]: 15,
  [PieceType.Crane]: 12,
  [PieceType.Frog]: 8,
  [PieceType.Fungi]: 5,
  [PieceType.Goat]: 20,
  [PieceType.Monkey]: 10,
  [PieceType.Octopus]: 18,
  [PieceType.Penguin]: 15,
  [PieceType.Rhino]: 20,
  [PieceType.Snake]: 12,
  [PieceType.Tree]: 3,
  [PieceType.Virus]: 8,
  [PieceType.Wolf]: 15,
};

const KING_VALUE = 500;

interface PhaseWeights {
  kingProximity: number;
  raceDifferential: number;
  nonKingAdvancement: number;
  escortBonus: number;
  kingExposurePenalty: number;
  pathClearance: number;
  enemyBlockers: number;
  distractionValue: number;
  pieceMaterial: number;
}

const PHASE_WEIGHTS: Record<string, PhaseWeights> = {
  opening: {
    kingProximity: 50,
    raceDifferential: 40,
    nonKingAdvancement: 200,
    escortBonus: 10,
    kingExposurePenalty: 500,
    pathClearance: 20,
    enemyBlockers: 10,
    distractionValue: 10,
    pieceMaterial: 80,
  },
  midgame: {
    kingProximity: 400,
    raceDifferential: 300,
    nonKingAdvancement: 100,
    escortBonus: 250,
    pathClearance: 200,
    kingExposurePenalty: 150,
    enemyBlockers: 150,
    distractionValue: 80,
    pieceMaterial: 30,
  },
  endgame: {
    kingProximity: 1000,
    raceDifferential: 800,
    nonKingAdvancement: 20,
    escortBonus: 300,
    kingExposurePenalty: 0,
    pathClearance: 600,
    enemyBlockers: 400,
    distractionValue: 150,
    pieceMaterial: 5,
  },
};

interface PieceInfo {
  piece: IPiece;
  row: number;
  col: number;
}

export class WispEvaluator {
  private moveNumber: number = 0;

  setMoveNumber(moveNum: number): void {
    this.moveNumber = moveNum;
  }

  /**
   * Evaluate position from the perspective of the current player.
   * Returns normalized score in [0, 1] range.
   */
  evaluate(gameState: GameState, isMaximizingLight: boolean): number {
    // Terminal states
    if (gameState.state === "Post Game" || gameState.state === "Terminated") {
      if (gameState.lightWon === true) return isMaximizingLight ? 1.0 : 0.0;
      if (gameState.lightWon === false) return isMaximizingLight ? 0.0 : 1.0;
      return 0.5; // draw
    }
    if (gameState.state === "Draw") return 0.5;

    const board = gameState.board;
    const boardHeight = board.length;     // 8
    const boardWidth = board[0].length;   // 5

    // Gather all pieces
    const ourPieces: PieceInfo[] = [];
    const enemyPieces: PieceInfo[] = [];
    let ourKing: PieceInfo | null = null;
    let enemyKing: PieceInfo | null = null;

    for (let row = 0; row < boardHeight; row++) {
      for (let col = 0; col < boardWidth; col++) {
        for (const piece of board[row][col].Pieces) {
          const isOurs = piece.isLight === isMaximizingLight;
          const info: PieceInfo = { piece, row, col };
          if (isOurs) {
            ourPieces.push(info);
            if (piece.isKing) ourKing = info;
          } else {
            enemyPieces.push(info);
            if (piece.isKing) enemyKing = info;
          }
        }
      }
    }

    // No king = loss
    if (!ourKing) return 0.0;
    if (!enemyKing) return 1.0;

    // Determine phase
    const phase = this.getPhase(ourPieces.length, enemyPieces.length);
    const w = PHASE_WEIGHTS[phase];

    let score = 0;

    // --- 1. King proximity to goal ---
    const ourGoalRow = isMaximizingLight ? 7 : 0;
    const enemyGoalRow = isMaximizingLight ? 0 : 7;
    const ourKingDist = Math.abs(ourGoalRow - ourKing.row);
    const enemyKingDist = Math.abs(enemyGoalRow - enemyKing.row);
    const ourKingProgress = 7 - ourKingDist; // 0 (at start) to 7 (at goal)
    score += ourKingProgress * w.kingProximity;

    // --- 2. Race differential ---
    const ourTurnsToWin = this.turnsToGoal(ourKing, isMaximizingLight);
    const enemyTurnsToWin = this.turnsToGoal(enemyKing, !isMaximizingLight);
    const raceDiff = enemyTurnsToWin - ourTurnsToWin; // positive = we're ahead
    score += raceDiff * w.raceDifferential;

    // --- 3. Non-king advancement ---
    const forwardDir = isMaximizingLight ? 1 : -1;
    for (const p of ourPieces) {
      if (p.piece.isKing) continue;
      const advancement = isMaximizingLight ? p.row : (7 - p.row);
      // Bonus for pieces on opposite flank from king (distraction)
      const isOppositeFlank = Math.abs(p.col - ourKing.col) >= 2;
      const flankMultiplier = isOppositeFlank ? 1.5 : 1.0;
      score += advancement * w.nonKingAdvancement * flankMultiplier * 0.1;
    }

    // --- 4. Escort bonus ---
    const nearbyFriends = this.countFriendsNearKing(ourKing, ourPieces, forwardDir);
    const kingAdvancement = isMaximizingLight ? ourKing.row : (7 - ourKing.row);
    score += kingAdvancement * nearbyFriends * w.escortBonus * 0.1;

    // --- 5. King exposure penalty ---
    if (w.kingExposurePenalty > 0 && kingAdvancement > 1) {
      const exposurePenalty = (kingAdvancement * 100) / (nearbyFriends + 1);
      score -= exposurePenalty * w.kingExposurePenalty * 0.01;
    }

    // --- 6. Path clearance ---
    const clearance = this.calculatePathClearance(
      board, ourKing, isMaximizingLight, boardHeight, boardWidth
    );
    score += clearance * w.pathClearance;

    // --- 7. Enemy blockers ---
    const blockerCount = this.countEnemyBlockers(
      board, ourKing, isMaximizingLight, enemyPieces, boardHeight
    );
    score -= blockerCount * w.enemyBlockers;

    // --- 8. Distraction value ---
    for (const p of ourPieces) {
      if (p.piece.isKing) continue;
      const isOppositeFlank = Math.abs(p.col - ourKing.col) >= 2;
      if (!isOppositeFlank) continue;
      // Check if near enemy pieces (drawing their attention)
      for (const e of enemyPieces) {
        const dist = Math.abs(p.row - e.row) + Math.abs(p.col - e.col);
        if (dist <= 2) {
          score += w.distractionValue;
          break; // One bonus per distraction piece
        }
      }
    }

    // --- 9. Piece material ---
    let ourMaterial = 0;
    let enemyMaterial = 0;
    for (const p of ourPieces) {
      ourMaterial += p.piece.isKing ? KING_VALUE : (PIECE_VALUES[p.piece.type] || 10);
    }
    for (const p of enemyPieces) {
      enemyMaterial += p.piece.isKing ? KING_VALUE : (PIECE_VALUES[p.piece.type] || 10);
    }
    score += (ourMaterial - enemyMaterial) * w.pieceMaterial * 0.01;

    // Normalize to [0, 1] using sigmoid
    return 1 / (1 + Math.exp(-score / 500));
  }

  private getPhase(ourCount: number, enemyCount: number): string {
    // Endgame if few pieces remain regardless of move count
    if (ourCount <= ENDGAME_PIECE_THRESHOLD || enemyCount <= ENDGAME_PIECE_THRESHOLD) {
      return 'endgame';
    }
    if (this.moveNumber <= OPENING_END) return 'opening';
    if (this.moveNumber <= MIDGAME_END) return 'midgame';
    return 'endgame';
  }

  /**
   * Estimate turns to reach goal row based on piece type movement speed.
   */
  private turnsToGoal(pieceInfo: PieceInfo, isLight: boolean): number {
    const goalRow = isLight ? 7 : 0;
    const distance = Math.abs(goalRow - pieceInfo.row);
    if (distance === 0) return 0;

    // Approximate max squares per turn for each piece type
    const speed: Partial<Record<PieceType, number>> = {
      [PieceType.Crane]: 2,
      [PieceType.Penguin]: 2,
      [PieceType.Snake]: 2,
      [PieceType.Rhino]: 2,
      [PieceType.Frog]: 2, // diagonal but still 2 sq
    };
    const maxPerTurn = speed[pieceInfo.piece.type] || 1;
    return Math.ceil(distance / maxPerTurn);
  }

  /**
   * Count friendly non-king pieces within 2 squares ahead/beside the king.
   */
  private countFriendsNearKing(
    king: PieceInfo,
    ourPieces: PieceInfo[],
    forwardDir: number
  ): number {
    let count = 0;
    for (const p of ourPieces) {
      if (p.piece.isKing) continue;
      const rowDiff = (p.row - king.row) * forwardDir; // positive = ahead of king
      const colDiff = Math.abs(p.col - king.col);
      // Within a 5x3 rectangle: 0-2 rows ahead, 1 col to each side
      if (rowDiff >= 0 && rowDiff <= 2 && colDiff <= 1) {
        count++;
      }
    }
    return count;
  }

  /**
   * Calculate path clearance: ratio of open squares in king's column ± 1 toward goal.
   * Returns value in roughly [−1, 1] range (negative if heavily blocked).
   */
  private calculatePathClearance(
    board: GameState['board'],
    king: PieceInfo,
    isLight: boolean,
    boardHeight: number,
    boardWidth: number
  ): number {
    const goalRow = isLight ? 7 : 0;
    const step = isLight ? 1 : -1;
    let clearSquares = 0;
    let totalSquares = 0;

    for (let dCol = -1; dCol <= 1; dCol++) {
      const col = king.col + dCol;
      if (col < 0 || col >= boardWidth) continue;

      let row = king.row + step;
      while (row >= 0 && row < boardHeight) {
        totalSquares++;
        const hasEnemy = board[row][col].Pieces.some(p => p.isLight !== isLight);
        if (hasEnemy) {
          clearSquares -= 2; // Blockers are extra bad
        } else {
          clearSquares++;
        }
        if (row === goalRow) break;
        row += step;
      }
    }

    return totalSquares > 0 ? clearSquares / totalSquares : 0;
  }

  /**
   * Count enemy pieces directly in the king's forward path (same or adjacent columns).
   */
  private countEnemyBlockers(
    board: GameState['board'],
    king: PieceInfo,
    isLight: boolean,
    enemyPieces: PieceInfo[],
    boardHeight: number
  ): number {
    let count = 0;
    const step = isLight ? 1 : -1;

    for (const e of enemyPieces) {
      const colDiff = Math.abs(e.col - king.col);
      if (colDiff > 1) continue; // Not in king's lane
      // Is the enemy piece ahead of our king?
      const isAhead = isLight ? e.row > king.row : e.row < king.row;
      if (isAhead) count++;
    }
    return count;
  }
}
